import { useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";
import { Field, lbSelect } from "./LogbookTaskCard";
import { importParseWorksheet, importBuildTokens, importBuildPlan, isoDate } from "./logbookUtils";

const CHUNK = 100;
const ddmmyyyy = (iso) => `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}`;
const fmtDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

/* Team board → Import Excel (Logbook admins). Daily Production Meeting
   workbook → preview (employee matching, target dates, what is skipped) →
   tasks, each scheduled for its own target date. Nothing is written until
   the preview is confirmed; re-importing a sheet skips rows already imported. */
export default function LogbookImport({ ctx, onClose }) {
  const { data, act } = ctx;
  const [st, setSt] = useState({ busy: false, msg: "", buf: null, sheets: [], sheet: "", parsed: null, tokens: [], result: null, error: "" });
  const patch = (p) => setSt((s) => ({ ...s, ...p }));

  const loadSheet = (buf, name) => {
    const wb = XLSX.read(buf, { type: "array", cellNF: true, sheets: [name] });
    const out = importParseWorksheet(XLSX, wb.Sheets[name]);
    return out.error ? { sheet: name, parsed: null, tokens: [], error: out.error, result: null }
      : { sheet: name, parsed: out, tokens: importBuildTokens(out, data.users), error: "", result: null };
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    patch({ busy: true, msg: "Reading Excel…", error: "", result: null, parsed: null });
    try {
      const buf = await file.arrayBuffer();
      const idx = XLSX.read(buf, { type: "array", sheets: [0] });
      const meta = idx.Workbook?.Sheets || [];
      const sheets = idx.SheetNames.map((n, i) => ({ name: n, hidden: !!meta[i]?.Hidden }));
      // Default sheet: today's date (e.g. "28-09-2026") if present, else the last visible one.
      const today = ddmmyyyy(data.serverDate || isoDate(new Date()));
      const visible = sheets.filter((s) => !s.hidden);
      const pick = sheets.find((s) => s.name.trim() === today) || visible[visible.length - 1] || sheets[0];
      patch({ busy: false, msg: "", buf, sheets, ...loadSheet(buf, pick.name) });
    } catch (err) {
      patch({ busy: false, msg: "", error: err.message || "Could not read that file" });
    }
  };

  const sample = () => {
    const d = (off) => { const x = new Date(); x.setDate(x.getDate() + off); return ddmmyyyy(isoDate(x)); };
    const first = (n) => (data.users[n % Math.max(data.users.length, 1)] || { name: "Employee" }).name.split(" ")[0];
    const ws = XLSX.utils.aoa_to_sheet([
      ["Daily Production Meeting"], [],
      ["DATE", "TO NO", "CUSTOMER NAME", "PART NUMBER", "ORD. QTY", "B/L", "remarks", "RESPONSIBILITY", "date"],
      [d(0), "TO-6101", "Northline Pneumatics", "PF-20-TE", 400, 120, "Priority order", first(3), d(0)],
      [d(0), "TO-6102", "Harbour Process Systems", "AL-40-PR-6M", 60, 60, "", `${first(4)} / ${first(6)}`, d(1)],
      [d(0), "TO-6103", "Kestrel Compressors", "BV-25-SS", 150, 30, "Awaiting valve bodies", first(6), d(2)],
      [d(0), "TO-6105", "Delta Fabrication", "CL-50-AL", 90, 90, "No target date yet", first(9), ""],
    ]);
    ws["!cols"] = [12, 10, 26, 16, 10, 8, 26, 22, 12].map((w) => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, d(0));
    XLSX.writeFile(wb, "logbook-sample-import.xlsx");
  };

  const plan = st.parsed ? importBuildPlan(st.parsed, st.tokens) : null;
  const run = async () => {
    if (!plan?.tasks.length) return ctx.toastError("Nothing to import yet — match at least one employee");
    if (!window.confirm(`Create ${plan.tasks.length} task(s) from sheet "${st.sheet}"?\n\nEach task is scheduled for its own target date. Existing tasks, employees and logins are not touched.`)) return;
    const total = { created: 0, duplicates: 0, invalid: [] };
    let error = "";
    for (let i = 0; i < plan.tasks.length; i += CHUNK) {
      patch({ busy: true, msg: `Importing… ${Math.min(i + CHUNK, plan.tasks.length)} / ${plan.tasks.length}` });
      const res = await act("import_tasks", { sheet: st.sheet, rows: plan.tasks.slice(i, i + CHUNK) }, null, { quiet: true });
      if (!res) { error = "Import stopped — anything imported before this is kept. Run it again to add the rest (duplicates are skipped)."; break; }
      total.created += res.created || 0;
      total.duplicates += res.duplicates || 0;
      (res.invalid || []).forEach((x) => total.invalid.push(x));
    }
    patch({ busy: false, msg: "", parsed: error ? st.parsed : null, tokens: error ? st.tokens : [], result: total, error });
    ctx.reload();
  };

  const today = data.serverDate || isoDate(new Date());
  const empOpts = data.users.slice().sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="lb-import">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Import Excel</h3>
        <Button size="sm" variant="outline" onClick={onClose}><X className="w-3.5 h-3.5 mr-1" />Close</Button>
      </div>
      <p className="text-sm text-slate-600">Upload the Daily Production Meeting workbook. Tasks are created from the <b>RESPONSIBILITY</b> column (employee) and the <b>date</b> column (target date), with the other columns saved as the task details. You'll see a preview before anything is created.</p>
      <div className="flex flex-wrap items-center gap-2">
        <input type="file" accept=".xlsx,.xlsm,.xls" onChange={onFile} disabled={st.busy} className="text-sm" data-testid="lb-import-file" />
        <Button size="sm" variant="outline" onClick={sample} disabled={st.busy}><Download className="w-3.5 h-3.5 mr-1" />Download a sample workbook</Button>
      </div>
      {st.busy && <div className="text-sm text-slate-600">{st.msg || "Working…"}</div>}
      {st.error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{st.error}</div>}
      {st.result && (
        <div className="rounded-md border border-green-200 bg-green-50 text-sm px-3 py-2 text-slate-700">
          <b className="text-green-700">{st.result.created} task(s) created</b>
          {st.result.duplicates ? ` · ${st.result.duplicates} already imported earlier (skipped)` : ""}
          {st.result.invalid.length ? <span className="text-red-600"> · {st.result.invalid.length} row(s) rejected</span> : ""}
          {st.result.invalid.length > 0 && <div className="mt-1 text-xs text-slate-500">{st.result.invalid.slice(0, 8).map((x) => <div key={x}>{x}</div>)}</div>}
          <div className="mt-1 text-xs text-slate-500">Tasks dated today appear for each employee now; later dates appear automatically on their target date.</div>
        </div>
      )}
      {st.sheets.length > 0 && !st.busy && (
        <Field label="Sheet to import" className="max-w-xs">
          <select className={lbSelect} value={st.sheet} onChange={(e) => patch(loadSheet(st.buf, e.target.value))}>
            {st.sheets.map((s) => <option key={s.name} value={s.name}>{s.name}{s.hidden ? " (hidden)" : ""}</option>)}
          </select>
        </Field>
      )}
      {plan && !st.busy && (
        <div className="space-y-3">
          <div className="text-sm">
            <b>{plan.tasks.length}</b> task(s) will be created from {plan.stat.rows} row(s).{" "}
            <span className="text-slate-500">Skipped: {plan.stat.noDate} without a target date{plan.stat.noEmployee ? `, ${plan.stat.noEmployee} without an employee` : ""}{plan.stat.notMatchedRows ? `, ${plan.stat.notMatchedRows} with no matched employee` : ""}.</span>
          </div>
          {st.tokens.length > 0 && (
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">Match Excel names to employees</div>
              <div className="rounded-md border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="text-left px-3 py-2">Name in Excel</th><th className="px-3 py-2">Rows</th><th className="text-left px-3 py-2">Employee</th></tr></thead>
                  <tbody>
                    {st.tokens.map((t, i) => (
                      <tr key={t.key} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-medium">{t.label}</td>
                        <td className="px-3 py-1.5 text-center font-mono">{t.count}</td>
                        <td className="px-3 py-1.5">
                          <select className={lbSelect} value={t.userId} onChange={(e) => patch({ tokens: st.tokens.map((x, j) => (j === i ? { ...x, userId: e.target.value } : x)) })}>
                            <option value="">— Skip these rows —</option>
                            {empOpts.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Names are matched to existing employees only — no employee is ever created. Anything left on "Skip" is not imported.</p>
            </div>
          )}
          {Object.keys(plan.byDate).length > 0 && (
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">Tasks by target date</div>
              {Object.keys(plan.byDate).sort().map((d) => (
                <div key={d} className="flex gap-3 text-sm py-0.5">
                  <span className="font-mono min-w-[150px]">{fmtDay(d)}</span>
                  <span className="font-mono min-w-[36px]">{plan.byDate[d]}</span>
                  <span className="text-slate-500">{d > today ? "appears on that date" : d === today ? "appears today" : "date already passed — appears now, marked overdue"}</span>
                </div>
              ))}
            </div>
          )}
          {st.parsed.fixedDates.length > 0 && (
            <p className="text-[11px] text-slate-500">Some date cells ({st.parsed.fixedDates.slice(0, 4).join(", ")}{st.parsed.fixedDates.length > 4 ? ", …" : ""}) were stored by Excel as month-day. They've been read day-month, the way they're written in the sheet — please confirm the dates above look right.</p>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={run} disabled={!plan.tasks.length} data-testid="lb-import-run">Import {plan.tasks.length} task(s)</Button>
            <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
